<script setup>
import { ref, computed } from 'vue'
import { AisIndex, AisAutocomplete, AisInstantSearch } from 'vue-instantsearch/vue3/es'
import {
  Command,
  CommandList,
  CommandGroup,
  CommandItem,
  CommandEmpty,
  CommandInput,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@codinglabsau/gooey'
import moment from 'moment'
import { Link, usePage } from '@inertiajs/vue3'
import { onKeyStroke } from '@vueuse/core'
import { Search as SearchIcon } from 'lucide-vue-next'
import { algoliaSearchClient } from '@/Composables/useAlgoliaSearch.js'

const searchClient = algoliaSearchClient

const open = ref(false)

const isMacKeyboard = computed(() => {
  return typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgent)
})

onKeyStroke('k', (e) => {
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault()
    open.value = true
  }
})

const getLabel = (indexName) => {
  switch (indexName) {
    case usePage().props.algolia.convicts_index:
      return 'Convicts'
    case usePage().props.algolia.crimes_index:
      return 'Crimes'
    case usePage().props.algolia.occupations_index:
      return 'Occupations'
    case usePage().props.algolia.ships_index:
      return 'Ships'
    case usePage().props.algolia.voyages_index:
      return 'Voyages'
    default:
      return ''
  }
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger class="w-full">
      <div
        class="flex w-full items-center justify-between gap-2 border-gray-200 px-2 py-1 text-sm text-muted-foreground sm:rounded sm:border"
      >
        <div class="flex items-center gap-2">
          <SearchIcon class="size-5 sm:size-4" />
          <span class="hidden lg:flex">Search</span>
        </div>

        <kbd
          class="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 text-xs text-muted-foreground opacity-100 sm:inline-flex"
        >
          <span v-if="isMacKeyboard" class="text-sm"> ⌘ </span>
          <span v-else> Ctrl </span>
          K
        </kbd>
      </div>
    </DialogTrigger>
    <DialogContent
      class="left-1/2 top-10 max-h-[65vh] -translate-x-1/2 translate-y-0 overflow-y-auto md:max-w-2xl"
    >
      <DialogHeader>
        <DialogTitle>Search</DialogTitle>
        <DialogDescription />
      </DialogHeader>
      <Command>
        <AisInstantSearch :search-client="searchClient" :index-name="$page.props.algolia.convicts_index">
          <AisIndex :index-name="$page.props.algolia.crimes_index" />
          <AisIndex :index-name="$page.props.algolia.occupations_index" />
          <AisIndex :index-name="$page.props.algolia.ships_index" />
          <AisIndex :index-name="$page.props.algolia.voyages_index" />
          <AisAutocomplete>
            <template #default="{ currentRefinement, indices, refine }">
              <CommandInput
                type="search"
                :value="currentRefinement"
                placeholder="Type a convict name, crime, ..."
                class="!border-0 focus:ring-0"
                @input="refine($event.currentTarget.value)"
              />
              <CommandList v-show="currentRefinement">
                <CommandEmpty
                  v-if="indices.every(index => index.hits.length === 0)"
                  class="text-gray-500"
                >
                  No results found.
                </CommandEmpty>

                <template v-for="index in indices" :key="index.indexId">
                  <CommandGroup as="div" class="py-2">
                    <div
                      v-show="index.hits.length > 0"
                      class="border-b border-primary/20 px-3 py-2 text-xs uppercase text-primary"
                    >
                      {{ getLabel(index.indexName) }}
                    </div>

                    <CommandItem
                      v-for="hit in index.hits"
                      :key="hit.objectID"
                      :as="Link"
                      :value="index.indexName + ':' + hit.objectID + ':' + hit.name"
                      class="flex items-center px-2 md:!max-w-2xl"
                      :href="hit.url"
                      @click="open = false"
                    >
                      <img
                        v-show="hit.image"
                        :src="hit.image"
                        :alt="hit.id"
                        class="mr-2 size-8 rounded-full"
                      />
                      <div class="flex flex-col">
                        {{ hit.name }}
                        <div
                          v-show="index.indexName == $page.props.algolia.convicts_index"
                          class="my-1 flow-root text-xs text-gray-500"
                        >
                          <div class="flex">
                            <div>
                              Date of Birth: {{
                                hit.date_of_birth
                                  ? moment(hit.date_of_birth).format('DD/MM/Y')
                                  : 'unknown'
                              }}
                            </div>
                            <div class="ml-2">
                              Date of Death: {{
                                hit.date_of_death
                                  ? moment(hit.date_of_death).format('DD/MM/Y')
                                  : 'unknown'
                              }}
                            </div>
                          </div>
                        </div>
                        <div v-show="index.indexName == $page.props.algolia.voyages_index">
                          {{
                            hit.arrival_date
                              ? moment(hit.arrival_date).format('DD/MM/Y')
                              : 'unknown'
                          }}
                          -
                          {{
                            hit.departure_date
                              ? moment(hit.departure_date).format('DD/MM/Y')
                              : 'unknown'
                          }}
                          <div class="text-xs text-gray-500">
                            {{ hit.ship ?? 'unknown' }} -
                            {{ hit.place_of_arrival?.name ?? 'unknown' }}
                          </div>
                        </div>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                </template>
              </CommandList>
            </template>
          </AisAutocomplete>
        </AisInstantSearch>
      </Command>
    </DialogContent>
  </Dialog>
</template>
